import { RUNNIT_NODE_DEF_TAG_TYPE, Avatar } from '@run-diffusion/shared';

// Options render an avatar to the left of the text in the picker via
// FilterOptionAvatar. Tools carry a resolved `avatar` (their node defs are
// vuexfire-bound); boards carry only `avatarId` and the cell $binds it lazily.
// Options with neither (dates, aspect ratios, tags) just fall back to text/icon.
export interface FacetOption {
	value: string;
	text: string;
	avatar?: Avatar | null;
	avatarId?: string | null;
	disabled?: boolean;
}

// Library scope for the Postgres filter endpoint. 'personal' is the caller's own
// runs; 'team' is one or more teams they manage (needs MANAGE_ALL_GENERATIONS),
// narrowed by the panel's team picker. Distinct from the host's nav tabs.
// Single source of truth is the REST client — imported and re-exported here so
// the draft types and the wire types can never silently diverge.
import type { LibraryScope } from './libraryApi';
export type { LibraryScope };

export interface LibraryFilterDraft {
	aspectRatioFilter: string;
	resolutionFilter: string;
	toolFilter: string;
	modelFamilyTagFilter: string;
	mediaTagFilter: string;
	dateFromFilter: string;
	dateToFilter: string;
	// Favorite overlay (personal only): '' = all, 'favorited' = only your results
	// you have favorited (same set as the Favorites tab, in library order).
	favoriteFilter: string;
	// Filter-by-user: the owning user's id (within the active scope).
	ownerFilter: string;
	// Filter-by-board: the runnit (board) id whose runs to show (within scope).
	boardFilter: string;
	// Scope selector + the teams picked under Team scope (empty = the active one).
	scopeFilter: LibraryScope;
	teamIdsFilter: string[];
	// Library Search: the committed term from the search box beside the panel.
	searchFilter: string;
}

export interface AppliedLibraryFilters {
	aspect: string;
	res: string;
	tool: string;
	family: string;
	media: string;
	from: string;
	to: string;
	favorite: string;
	owner: string;
	board: string;
	scope: LibraryScope;
	teamIds: string[];
	search: string;
}

// --- the filter registry ----------------------------------------------------
//
// Every library filter is declared exactly once, here. The draft shape (what the
// panel's controls bind to), the applied shape (what hosts hand to the gallery),
// the URL query, the "is anything filtering?" predicate, and the scroller
// invalidation key are all DERIVED from this table. Adding or removing a filter
// is a one-line change; there is no second place to miss.
//
// The `Record<keyof LibraryFilterDraft, ...>` annotation is the enforcement: omit
// a draft field and the file does not compile.

export type LibraryFilterFieldKind = 'string' | 'stringArray' | 'scope';

export interface LibraryFilterField {
	// Key on LibraryFilterDraft (the panel's control state).
	draftKey: keyof LibraryFilterDraft;
	// Key on AppliedLibraryFilters (the committed snapshot hosts pass down).
	appliedKey: keyof AppliedLibraryFilters;
	// URL query parameter name. Absent means deliberately never serialized: owner
	// and board are opaque ids we keep out of shareable links, and scope/teamIds
	// select which set to look at rather than narrowing it.
	queryKey?: string;
	// Whether this field counts toward "is anything filtering?".
	//
	// Scope and teamIds are excluded on purpose. Counting them (scope is always a
	// non-empty enum, teamIds is always an array, both truthy) is exactly what made
	// the header badge read "2" and the filter icon light up on a pristine library.
	narrowing: boolean;
	// Fields rendered as one control share a count group. The two date bounds,
	// for example, contribute one badge count whether one or both are set.
	countGroup?: string;
	// The Library Search term has its own box beside the panel rather than a
	// control in it. It still narrows (routing, the URL, the remount key), but the
	// filter badge counts leave it out, and hosts carry it across the panel's
	// Apply and Clear all, whose snapshots come from a draft that never sees it.
	outsidePanel?: boolean;
	kind: LibraryFilterFieldKind;
}

const LIBRARY_FILTER_FIELD_BY_DRAFT_KEY: Record<keyof LibraryFilterDraft, LibraryFilterField> = {
	aspectRatioFilter:    { draftKey: 'aspectRatioFilter',    appliedKey: 'aspect',   queryKey: 'aspect',   narrowing: true,  kind: 'string' },
	resolutionFilter:     { draftKey: 'resolutionFilter',     appliedKey: 'res',      queryKey: 'res',      narrowing: true,  kind: 'string' },
	toolFilter:           { draftKey: 'toolFilter',           appliedKey: 'tool',     queryKey: 'tool',     narrowing: true,  kind: 'string' },
	modelFamilyTagFilter: { draftKey: 'modelFamilyTagFilter', appliedKey: 'family',   queryKey: 'family',   narrowing: true,  kind: 'string' },
	mediaTagFilter:       { draftKey: 'mediaTagFilter',       appliedKey: 'media',    queryKey: 'media',    narrowing: true,  kind: 'string' },
	dateFromFilter:       { draftKey: 'dateFromFilter',       appliedKey: 'from',     queryKey: 'from',     narrowing: true,  countGroup: 'date', kind: 'string' },
	dateToFilter:         { draftKey: 'dateToFilter',         appliedKey: 'to',       queryKey: 'to',       narrowing: true,  countGroup: 'date', kind: 'string' },
	favoriteFilter:       { draftKey: 'favoriteFilter',       appliedKey: 'favorite', queryKey: 'favorite', narrowing: true,  kind: 'string' },
	ownerFilter:          { draftKey: 'ownerFilter',          appliedKey: 'owner',                          narrowing: true,  kind: 'string' },
	boardFilter:          { draftKey: 'boardFilter',          appliedKey: 'board',                          narrowing: true,  kind: 'string' },
	scopeFilter:          { draftKey: 'scopeFilter',          appliedKey: 'scope',                          narrowing: false, kind: 'scope' },
	teamIdsFilter:        { draftKey: 'teamIdsFilter',        appliedKey: 'teamIds',                        narrowing: false, kind: 'stringArray' },
	searchFilter:         { draftKey: 'searchFilter',         appliedKey: 'search',   queryKey: 'search',   narrowing: true,  outsidePanel: true, kind: 'string' },
};

export const LIBRARY_FILTER_FIELDS: LibraryFilterField[] = Object.values(LIBRARY_FILTER_FIELD_BY_DRAFT_KEY);

const NARROWING_FIELDS = LIBRARY_FILTER_FIELDS.filter((f) => f.narrowing);
const PANEL_NARROWING_FIELDS = NARROWING_FIELDS.filter((f) => !f.outsidePanel);
const QUERY_FIELDS = LIBRARY_FILTER_FIELDS.filter((f) => !!f.queryKey);

// Normalize an arbitrary incoming value to the field's declared kind, so a
// missing/garbage value (a stale URL, a partially-seeded snapshot) degrades to
// the field's empty state instead of leaking undefined into the controls.
export function normalizeLibraryFilterValue (field: LibraryFilterField, value: any): any {
	if (field.kind === 'stringArray') return Array.isArray(value) ? value : [];
	if (field.kind === 'scope') return (value || 'personal') as LibraryScope;
	return value || '';
}

export function createEmptyLibraryFilterDraft (): LibraryFilterDraft {
	const draft = {} as LibraryFilterDraft;
	LIBRARY_FILTER_FIELDS.forEach((f) => {
		(draft as any)[f.draftKey] = normalizeLibraryFilterValue(f, undefined);
	});
	return draft;
}

export function createEmptyAppliedLibraryFilters (): AppliedLibraryFilters {
	const applied = {} as AppliedLibraryFilters;
	LIBRARY_FILTER_FIELDS.forEach((f) => {
		(applied as any)[f.appliedKey] = normalizeLibraryFilterValue(f, undefined);
	});
	return applied;
}

// Frozen singletons for read-only comparison. Use the factories above whenever
// the result will be mutated — a spread of the frozen object still shares its
// teamIdsFilter array.
export const EMPTY_LIBRARY_FILTER_DRAFT: LibraryFilterDraft = Object.freeze(createEmptyLibraryFilterDraft());
export const EMPTY_APPLIED_LIBRARY_FILTERS: AppliedLibraryFilters = Object.freeze(createEmptyAppliedLibraryFilters());

// The draft/applied fields that actually narrow the result set. Derived, not
// typed out — see the `narrowing` flag in the registry for the rationale.
export const NARROWING_LIBRARY_FILTER_KEYS: (keyof LibraryFilterDraft)[] = NARROWING_FIELDS.map((f) => f.draftKey);
export const NARROWING_APPLIED_FILTER_KEYS: (keyof AppliedLibraryFilters)[] = NARROWING_FIELDS.map((f) => f.appliedKey);

// True when any narrowing library filter holds a value.
export function hasAnyLibraryFilter (filters: Partial<LibraryFilterDraft>): boolean {
	return NARROWING_LIBRARY_FILTER_KEYS.some((key) => !!filters[key]);
}

// Same predicate over the APPLIED shape, for hosts that hold the committed
// snapshot rather than the panel's draft. Both hosts previously hand-rolled this
// as an `a.aspect || a.res || ...` chain and had already drifted into two
// different spellings of it.
export function hasAnyAppliedLibraryFilter (applied: Partial<AppliedLibraryFilters>): boolean {
	return NARROWING_APPLIED_FILTER_KEYS.some((key) => !!applied[key]);
}

function countActiveGroups (
	fields: LibraryFilterField[],
	valueFor: (field: LibraryFilterField) => unknown,
): number {
	const groups = new Set<string>();
	fields.forEach((field) => {
		if (valueFor(field)) groups.add(field.countGroup || String(field.appliedKey));
	});
	return groups.size;
}

// How many of the panel's narrowing controls hold a value, with paired storage
// fields such as the date bounds contributing one group.
export function countActiveLibraryFilters (filters: Partial<LibraryFilterDraft>): number {
	return countActiveGroups(PANEL_NARROWING_FIELDS, (field) => filters[field.draftKey]);
}

// The same grouped count over the committed shape. Hosts use this for badges so
// an in-progress panel draft can never change the applied count.
export function countAppliedLibraryFilters (applied: Partial<AppliedLibraryFilters>): number {
	return countActiveGroups(PANEL_NARROWING_FIELDS, (field) => applied[field.appliedKey]);
}

// The scroller-invalidation key: remounting VirtualScrollResults on a key change
// is what makes the grid refetch from page 1. Covers every narrowing filter PLUS
// scope and the picked team set — scope/teamIds don't count as "filters"
// (see NARROWING_LIBRARY_FILTER_KEYS), but changing either changes which results
// the next fetch should return, so it must invalidate. The team set is sorted so
// reordering the same ids doesn't force a needless remount.
//
// NOTE: including scope means a scope-only Apply with no narrowing filters also
// remounts and re-runs the (Firestore) fetch. That redundant refetch is expected
// and harmless — do not condition the key on isFilterActive, which would
// reintroduce the stale-scope bug this exists to fix.
export function buildLibraryFilterKey (filters: Partial<LibraryFilterDraft>): string {
	return LIBRARY_FILTER_FIELDS.map((f) => {
		const value = normalizeLibraryFilterValue(f, (filters as any)[f.draftKey]);
		return f.kind === 'stringArray' ? [...value].sort().join(',') : String(value);
	}).join('|');
}

export function buildAppliedLibraryFilters (draft: Partial<LibraryFilterDraft>): AppliedLibraryFilters {
	const applied = {} as AppliedLibraryFilters;
	LIBRARY_FILTER_FIELDS.forEach((f) => {
		(applied as any)[f.appliedKey] = normalizeLibraryFilterValue(f, (draft as any)[f.draftKey]);
	});
	return applied;
}

// The inverse: seed the panel's draft controls from a committed snapshot.
export function buildDraftFromApplied (applied: Partial<AppliedLibraryFilters>): LibraryFilterDraft {
	const draft = {} as LibraryFilterDraft;
	LIBRARY_FILTER_FIELDS.forEach((f) => {
		(draft as any)[f.draftKey] = normalizeLibraryFilterValue(f, (applied as any)[f.appliedKey]);
	});
	return draft;
}

// Serialize the applied snapshot to the URL query, omitting empty values and any
// field the registry marks as URL-exempt (owner/board ids, scope/teamIds).
export function buildLibraryFilterQuery (applied: Partial<AppliedLibraryFilters>): Record<string, string> {
	const query: Record<string, string> = {};
	QUERY_FIELDS.forEach((f) => {
		const value = (applied as any)[f.appliedKey];
		if (value) query[f.queryKey as string] = String(value);
	});
	return query;
}

// Read an applied snapshot back out of the URL query. Fields with no queryKey
// come back at their empty default rather than being dropped from the object,
// which is what made the Library page's hand-rolled seed lose owner/board/scope/
// teamIds on load.
export function readAppliedLibraryFiltersFromQuery (query: Record<string, any>): AppliedLibraryFilters {
	const applied = createEmptyAppliedLibraryFilters();
	QUERY_FIELDS.forEach((f) => {
		const raw = (query || {})[f.queryKey as string];
		if (typeof raw === 'string' && raw) (applied as any)[f.appliedKey] = raw;
	});
	return applied;
}

export function clearAndApplyLibraryFilters (): {
	draft: LibraryFilterDraft;
	applied: AppliedLibraryFilters;
} {
	const draft = createEmptyLibraryFilterDraft();
	return {
		draft,
		applied: buildAppliedLibraryFilters(draft),
	};
}

/**
 * Reconcile a Team picker proposal against the memberships the caller may
 * actually view, returning the ids to keep and the locked options that were
 * refused (so the caller can explain the refusal).
 *
 * Locked memberships stay selectable in the picker on purpose: they remain
 * discoverable, and activating one is what triggers the explanation. That makes
 * every proposal untrusted, so it is reconciled here rather than accepted.
 *
 * Always returns a NEW array, even when nothing was stripped. The picker is a
 * controlled component that has already rendered the chip it proposed, and only
 * a changed value identity makes it drop that chip again.
 */
export function reconcileTeamSelection (
	proposedIds: string[],
	teamOptions: FacetOption[],
): { teamIds: string[]; rejected: FacetOption[] } {
	const optionByValue = new Map<string, FacetOption>(
		(teamOptions || []).map((option) => [option.value, option] as [string, FacetOption]),
	);
	const teamIds: string[] = [];
	const rejected: FacetOption[] = [];
	(proposedIds || []).forEach((id: string) => {
		const option = optionByValue.get(id);
		// An id with no option behind it (the roster changed mid-pick) is dropped
		// silently: there is nothing to name in an explanation.
		if (!option) return;
		if (option.disabled) rejected.push(option);
		else teamIds.push(id);
	});
	return { teamIds, rejected };
}

// Must match the sync-computed Postgres facet column values (derived_aspectRatioLabel),
// i.e. the canonical shared/src/aspectRatio buckets (ADR 0018).
export const ASPECT_RATIO_FILTER_OPTIONS: FacetOption[] = [
	{ value: '1:1', text: '1:1 (Square)' },
	{ value: '16:9', text: '16:9 (Landscape)' },
	{ value: '2:1', text: '2:1' },
	{ value: '3:2', text: '3:2' },
	{ value: '4:3', text: '4:3' },
	{ value: '21:9', text: '21:9 (Ultrawide)' },
	{ value: '9:16', text: '9:16 (Portrait)' },
	{ value: '2:3', text: '2:3' },
	{ value: '3:4', text: '3:4' },
	{ value: 'OTHER', text: 'Other' },
];

export const RESOLUTION_FILTER_OPTIONS: FacetOption[] = [
	{ value: '1K', text: '1K (~1024px)' },
	{ value: '2K', text: '2K (~2048px)' },
	{ value: '4K+', text: '4K+ (4096px and up)' },
];

// Favorite overlay (personal). 'All' clears it; 'Favorited' narrows to results the
// caller has favorited (same set as the Favorites tab, in library order).
export const FAVORITE_FILTER_OPTIONS: FacetOption[] = [
	{ value: '', text: 'All' },
	{ value: 'favorited', text: 'Favorited' },
];

interface TagLike { id: string; label: string; type: string; sortOrder?: number; isDeleted?: boolean; }

export type GroupedFilterTags = {
	MODEL_FAMILY: FacetOption[];
	MEDIA: FacetOption[];
};

/** Group public node-def tags into the filterable categories. */
export function groupFilterTagsByType (tagsMap: Record<string, TagLike>): GroupedFilterTags {
	const wanted: (keyof GroupedFilterTags)[] = [
		RUNNIT_NODE_DEF_TAG_TYPE.MODEL_FAMILY,
		RUNNIT_NODE_DEF_TAG_TYPE.MEDIA,
	] as (keyof GroupedFilterTags)[];

	const grouped: GroupedFilterTags = { MODEL_FAMILY: [], MEDIA: [] };
	const tags = Object.values(tagsMap || {})
		.filter((t) => t && !t.isDeleted && wanted.includes(t.type as keyof GroupedFilterTags))
		.sort((a, b) => {
			const so = (a.sortOrder || 0) - (b.sortOrder || 0);
			return so !== 0 ? so : (a.label || '').localeCompare(b.label || '');
		});
	for (const t of tags) {
		grouped[t.type as keyof GroupedFilterTags].push({ value: t.id, text: t.label });
	}
	return grouped;
}
